import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

export type AuthType = 'api_key' | 'oauth2' | 'basic' | 'none'

export type Source = {
  id: string
  key: string
  name: string
  version: string
  authType: AuthType
  configSchema: Record<string, unknown>
  secretSchema: Record<string, unknown>
  category: string
  defaultProtocol: string | null
  defaultDirection: string
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`)
  }
  return res.json() as Promise<T>
}

export type CreateSourcePayload = {
  name: string
  key: string
  category?: string
  defaultProtocol?: string
  defaultDirection?: string
  authType?: AuthType
}

// The key is immutable, so it is intentionally absent here.
export type UpdateSourcePayload = {
  name?: string
  category?: string
  defaultProtocol?: string
  defaultDirection?: string
  authType?: AuthType
}

export const sourcesApi = {
  list: () => request<Source[]>('/sources'),

  create: (payload: CreateSourcePayload) =>
    request<Source>('/sources', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (id: string, payload: UpdateSourcePayload) =>
    request<Source>(`/sources/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  remove: (id: string) =>
    request<Source>(`/sources/${id}`, {
      method: 'DELETE',
    }),
}
